import * as ethers from 'ethers'
import { bn } from 'shared/utils/bignumber'
import type { UseFormReturn } from 'react-hook-form'

import { useCallback, useState } from 'react'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useFormLock } from 'shared/hooks/useFormLock'
import { useTransactionSender } from 'modules/blockChain/hooks/useTransactionSender'
import { useGasEstimateResets } from 'modules/pools/hooks/useGasEstimateResets'

import type { FormValues, SuccessData } from './types'
import { logGroup } from 'shared/utils/logGroup'
import { strToWei, weiToStr } from 'modules/blockChain/utils/parseWei'
import { getPoolInvestorByAddress } from 'modules/pools/utils/getPoolInvestorContract'
import * as errors from 'shared/constants/errors'
import { useFirebase } from 'modules/analytics/firebase'
import {
  DEPOSIT_WITHDRAWAL_CONFIRM_CLICK_EVENT,
  DEPOSIT_WITHDRAWAL_CONFIRM_FAIL_EVENT,
  DEPOSIT_WITHDRAWAL_CONFIRM_SUCCESS_EVENT,
  DEPOSIT_WITHDRAWAL_EDIT_CLICK_EVENT,
  DEPOSIT_WITHDRAWAL_SUBMIT_CLICK_EVENT,
} from 'modules/analytics/events'
import { useAssetBalance } from 'modules/pools/hooks/useAssetBalance'
import { PoolAsset } from 'modules/pools/constants/poolAssets'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'

type Args = {
  poolAddress: string
  onSuccess: (res: SuccessData) => void
  formMethods: UseFormReturn<FormValues>
  debtTokenDecimals: number
}

export function useWithdrawalDepositSubmit({
  poolAddress,
  onSuccess,
  formMethods,
  debtTokenDecimals,
}: Args) {
  const { chainId, walletAddress, library } = useWeb3()
  const [isSubmitting, setSubmitting] = useState(false)
  const { isLocked, lockForm, unlockForm } = useFormLock()
  const { trackEvent } = useFirebase()
  const { data: WETHBalanceWei } = useAssetBalance(PoolAsset.WETH)
  const { getAssetDecimals } = useAssetsData()

  /**
   * Gather Data
   */
  const gatherData = useCallback(() => {
    if (!walletAddress) throw new Error(errors.connectWallet)
    const { amountPercents, liquidity } = formMethods.getValues()

    const liquidityBn = new bn(liquidity)
    const amountPercentsNmb = Number(amountPercents)
    const amountBn = liquidityBn
      .times(amountPercentsNmb / 100)
      .toFixed(debtTokenDecimals)

    return {
      amountBn,
    }
  }, [formMethods, walletAddress, debtTokenDecimals])

  /**
   * Withdrawal tx
   */
  const populateWithdrawal = useCallback(async () => {
    const { amountBn } = gatherData()
    const Pool = getPoolInvestorByAddress(chainId, poolAddress)
    const pool = Pool.contract.connectWeb3({ chainId, library })
    const decimals = await pool.decimals()
    const amountWei = strToWei(amountBn, decimals)

    logGroup('Submitting deposit withdrawal', {
      Amount: ethers.utils.formatEther(amountWei),
      'Amount wei': amountWei.toString(),
      'Pool address': poolAddress,
    })

    const populated = await pool.populateTransaction.withdrawalPool(
      amountWei,
      Pool.contract.version!,
      {
        gasLimit: 500000,
      },
    )
    return populated
  }, [gatherData, poolAddress, chainId, library])
  const txWithdrawal = useTransactionSender(populateWithdrawal)

  const {
    send: sendWithdrawal,
    estimateGas,
    estimatedGas,
    resetGas,
  } = txWithdrawal

  /**
   * Form submitter
   */
  const submit = useCallback(
    async (formValues: FormValues) => {
      try {
        const { asset, depositedAmount, amountPercents } = formValues
        if (!isLocked) {
          lockForm()
          trackEvent(DEPOSIT_WITHDRAWAL_SUBMIT_CLICK_EVENT, {
            asset,
            deposited_amount: depositedAmount,
            amount_percents: amountPercents,
          })
          await estimateGas()
          return
        }
        setSubmitting(true)
        const WETHDecimals = getAssetDecimals(PoolAsset.WETH)
        trackEvent(DEPOSIT_WITHDRAWAL_CONFIRM_CLICK_EVENT, {
          asset,
          deposited_amount: depositedAmount,
          amount_percents: amountPercents,
          weth_balance: WETHBalanceWei
            ? weiToStr(WETHBalanceWei, WETHDecimals)
            : undefined,
        })
        const txWithdrawalRes = await sendWithdrawal()
        setSubmitting(false)
        onSuccess({ tx: txWithdrawalRes, formValues })
        trackEvent(DEPOSIT_WITHDRAWAL_CONFIRM_SUCCESS_EVENT)
      } catch (err) {
        setSubmitting(false)
        trackEvent(DEPOSIT_WITHDRAWAL_CONFIRM_FAIL_EVENT)
        throw err
      }
    },
    [
      WETHBalanceWei,
      isLocked,
      getAssetDecimals,
      trackEvent,
      sendWithdrawal,
      onSuccess,
      lockForm,
      estimateGas,
    ],
  )

  useGasEstimateResets(isLocked, resetGas)

  const handleFormUnlock = useCallback(() => {
    unlockForm()

    const { amountPercents, depositedAmount, asset } = formMethods.getValues()
    trackEvent(DEPOSIT_WITHDRAWAL_EDIT_CLICK_EVENT, {
      asset,
      deposited_amount: depositedAmount,
      amount_percents: amountPercents,
    })
  }, [formMethods, trackEvent, unlockForm])

  return {
    submit,
    txWithdrawal,
    isSubmitting,
    isLocked,
    estimatedGas,
    lockForm,
    unlockForm: handleFormUnlock,
  }
}
